import dayjs from 'dayjs';

import type { SignRequest } from '@repo/api-types/docusign';
import { translate } from '@repo/phrase';
import type { SignRequestRow } from '@repo/types/docusign';

const computeStatusDate = (signRequest: SignRequest): string => {
    const status = mapStatus(signRequest.status);

    if (status === 'SIGNED' && signRequest.statusChangedDateTime) {
        return signRequest.statusChangedDateTime;
    }

    if (status === 'DECLINED' && signRequest.statusChangedDateTime) {
        return signRequest.statusChangedDateTime;
    }

    const first = signRequest.recipients?.signers?.[0];
    return first?.sentDateTime ?? signRequest.statusDateTime ?? '';
};

const mapStatus = (status?: string): SignRequestRow['status'] => {
    const statusValue = (status ?? '').toLowerCase();
    if (statusValue === 'completed') {
        return 'SIGNED';
    }
    if (statusValue === 'declined') {
        return 'DECLINED';
    }
    if (statusValue === 'voided') {
        return 'FAILED';
    }
    if (statusValue === 'expired') {
        return 'EXPIRED';
    }
    return 'PENDING';
};

export const getStatusDateLabel = (status: SignRequestRow['status']): string => {
    if (status === 'SIGNED') {
        return translate('docusign_signed_at');
    }
    if (status === 'DECLINED') {
        return translate('docusign_declined_at');
    }
    return translate('docusign_sent_at');
};

const extractTemplateName = (signRequest: SignRequest): string => {
    const customFields = signRequest.customFields?.textCustomFields;

    const templateName = customFields?.find((field) => field.name === 'templateName')?.value;

    if (templateName) {
        return templateName;
    }

    const templateId = customFields?.find((field) => field.name === 'templateId')?.value;

    if (templateId) {
        return templateId;
    }

    const envelopeId = signRequest.id ? signRequest.id.slice(0, 8) : '';
    return envelopeId
        ? `${translate('docusign_document_with_id')} ${envelopeId}`
        : translate('docusign_unknown_document');
};

export const toStatusViewRow = (signRequest: SignRequest): SignRequestRow => {
    const firstSigner = signRequest.recipients?.signers?.[0];
    const email = firstSigner?.email ?? '';
    const status = mapStatus(signRequest.status);
    const statusDate = computeStatusDate(signRequest);
    const documentLabel = extractTemplateName(signRequest);

    return {
        id: String(signRequest.id),
        documentLabel,
        status,
        statusDate,
        email,
    };
};

export const isValidEmail = (value: string) => /\S+@\S+\.\S+/.test(value.trim());

export const getDocusignFromDateValue = (creationDate?: string): string => {
    if (!creationDate) {
        return '';
    }
    return dayjs(creationDate).format('YYYY-MM-DD');
};
